import { getStatsigStableId } from './statsig';
import { transformLeadPayload } from './leadPayload';

const flowKey = () => `quote_submission:${getStatsigStableId()}`;
const transportFields = new Set([
  'resume_snapshot', 'resume_token',
  'contactId', 'contact_id', 'submission_revision', 'client_ip_address', 'client_user_agent',
  'user_agent', 'ga4_id', 'ga_session_id', 'gclid', 'msclkid', 'wbraid', 'gbraid', 'fbclid',
  'referrer', 'landing_page', 'meta_fbp', 'meta_fbc', 'statsig_user_id', 'autoSubmitReason', 'split_qa_token',
]);

function canonical(value) {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.keys(value).sort().map(key => [key, canonical(value[key])]));
  }
  return value;
}

function requestMaterial(payload) {
  const material = transformLeadPayload(payload, payload.site_origin);
  Object.keys(material).forEach(key => {
    if (transportFields.has(key) || key.startsWith('utm_')) delete material[key];
  });
  return JSON.stringify(canonical(material));
}

function readRevision(key, id) {
  const stored = sessionStorage.getItem(`${key}:revision`);
  if (stored === null) return null;
  const previous = JSON.parse(stored);
  if (!previous || previous.submission_id !== id || !Number.isSafeInteger(previous.revision)
    || previous.revision < 1 || typeof previous.material !== 'string' || typeof previous.manual_started !== 'boolean') {
    throw new Error('We could not restore your request for retry. Please contact us.');
  }
  return previous;
}

export function canAutoSubmitQuote() {
  try {
    const key = flowKey();
    return !readRevision(key, sessionStorage.getItem(key))?.manual_started;
  } catch { return false; }
}

// One flow key survives autosave, manual acceptance, retries and reloads.
// The server owns intent phases, payload binding and durable idempotence.
export function stampQuoteSubmission(payload) {
  const key = flowKey();
  let id = sessionStorage.getItem(key);
  if (!id) {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    id = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    sessionStorage.setItem(key, id);
    if (sessionStorage.getItem(key) !== id) throw new Error('We could not save your request for retry. Please retry.');
  }
  const stamped = { ...payload, submission_contract: 1, submission_id: id, autoSubmitted: payload.autoSubmitted === true };
  const revisionKey = `${key}:revision`;
  const previous = readRevision(key, id);
  if (stamped.autoSubmitted && previous?.manual_started) throw new Error('Your manual request is awaiting confirmation.');
  const material = requestMaterial(stamped);
  const revision = previous?.material === material ? previous.revision : (previous?.revision || 0) + 1;
  if (!Number.isSafeInteger(revision)) throw new Error('We could not save your request for retry. Please contact us.');
  if (!previous || previous.material !== material) {
    const journal = JSON.stringify({ submission_id: id, revision, material, manual_started: !stamped.autoSubmitted });
    sessionStorage.setItem(revisionKey, journal);
    if (sessionStorage.getItem(revisionKey) !== journal) throw new Error('We could not save your request for retry. Please retry.');
  }
  return { ...stamped, submission_revision: revision };
}

const validId = id => /^(?:[1-9]\d*)$/.test(String(id)) && Number.isSafeInteger(Number(id));

export function isConfirmedSubmission(result, payload) {
  if (result?.success !== true || !validId(result.deal_id) || !validId(result.contact_id)) return false;
  const receipt = result.receipt;
  if (payload.submission_revision !== undefined && (!Number.isSafeInteger(payload.submission_revision)
    || payload.submission_revision < 1 || receipt?.submission_revision !== payload.submission_revision)) return false;
  return receipt?.version === 1 && receipt.submission_id === payload.submission_id
    && (payload.autoSubmitted === true
      ? receipt.status === 'draft_saved' && receipt.intentional_confirmed === false
      : receipt.status === 'confirmed' && receipt.intentional_confirmed === true && receipt.event_owner === 'server');
}

export function markQuoteSubmissionConfirmed(payload, result) {
  if (payload.autoSubmitted || !isConfirmedSubmission(result, payload)) return;
  try { sessionStorage.setItem(`${flowKey()}:confirmed`, payload.submission_id); }
  catch { /* A local marker failure cannot undo a confirmed server receipt. */ }
}

export function beginQuoteSubmission() {
  try {
    const key = flowKey();
    if (sessionStorage.getItem(`${key}:confirmed`) === sessionStorage.getItem(key)) {
      sessionStorage.removeItem(`${key}:revision`);
      sessionStorage.removeItem(key);
      sessionStorage.removeItem(`${key}:confirmed`);
    }
  } catch { /* Submission will check durable retry storage before sending. */ }
}
